import type { HudStore } from "./hudStore";
import type { Badge, Quest } from "../types";
import { DOMAINS, BADGES, ATTRS, TITLES, TITLE_LVLS, titleFor, xpNeeded, heatColor, gemFill, slug, isoToday } from "../data/seed";
import { canPasskey } from "../lib/config";
import { tokenGet } from "../lib/api";
import { passkeyEnrolled } from "../lib/prefs";
import { dailyRewardEligible, servesMain, restsToday } from "../lib/quests";
import { isReceivableDebt } from "../lib/financeAnalytics";

// computeView = a 1:1 port of app.html computed() (lines 365–487): the single
// view-model the HUD consumes. Pure function of the store snapshot. Consume with
// `const v = useMemo(() => computeView(s), [s])`.

const liveQuests = (s: HudStore) => s.quests.filter((q) => !s.done[q.id]);
// Finished TODAY, not finished ever. Since live.ts hydrates the whole completed
// history into `quests`, anything that means "today" has to say so: the board's
// done shelf is labelled "N done today" and the daily-reward gate gives +40 for
// showing up today. A quest completed in-session carries `completed`; one that
// predates the session carries the server's `done_at` in the same field.
const doneToday = (s: HudStore, q: { id: string; completed?: string }): boolean =>
  !!s.done[q.id] && (q.completed || "").slice(0, 10) === isoToday();

// XP actually banked in a domain today - the per-domain slice of the character
// header's "XP today". The domain page's tile carries that same label and used
// to render domainXp instead, so finishing the only quest in a domain dropped
// the tile to 0 on the very day it was earned: the open pile emptied and there
// was nothing counting what came out of it. Credit comes from `awarded`, which
// holds what was really paid out (steps can pay part of a quest early), falling
// back to face value for a quest completed before that map existed.
export const completedTodayIn = (s: HudStore, name: string): Quest[] =>
  s.quests.filter((q) => q.domain === name && doneToday(s, q));

export const xpEarnedToday = (s: HudStore, name: string) =>
  completedTodayIn(s, name).reduce((t, q) => t + (s.awarded[q.id] ?? q.xp), 0);

function computeBadgesLive(s: HudStore): Badge[] {
  const lvl = s.level || 1;
  const streak = s.streak || 0;
  const debts = (s.debts || []).filter((d) => !isReceivableDebt(d));
  const owing = debts.reduce((t, d) => t + (d.balance || 0), 0);
  const started = debts.reduce((t, d) => t + (d.start || d.balance || 0), 0);
  const cleared = started ? Math.round((1 - owing / started) * 100) : 0;
  const hasDebt = debts.length > 0;
  const rules: Record<string, () => { earned: boolean; pct: number; at: string }> = {
    "Seven-day streak": () => ({ earned: streak >= 7, pct: Math.min(100, Math.round((streak / 7) * 100)), at: streak + " / 7 days" }),
    "Thirty-day streak": () => ({ earned: streak >= 30, pct: Math.min(100, Math.round((streak / 30) * 100)), at: streak + " / 30 days" }),
    "Level ten": () => ({ earned: lvl >= 10, pct: Math.min(100, Math.round((lvl / 10) * 100)), at: "level " + lvl + " / 10" }),
    Architect: () => ({ earned: lvl >= 9, pct: Math.min(100, Math.round((lvl / 9) * 100)), at: "level " + lvl + " / 9" }),
    "Debt under 60%": () => ({ earned: hasDebt && cleared >= 40, pct: Math.min(100, Math.round((cleared / 40) * 100)), at: hasDebt ? cleared + "% cleared" : "no debt data yet" }),
    // xp-rules.md defines `backlog-slayer` as five games beaten. It had neither a
    // rule nor a gem in BADGES, so it was defined in the spec and absent from the
    // product. Counts `beaten` only: `dropped` is a decision, not an achievement.
    "Backlog slayer": () => {
      const beaten = (s.games || []).filter((g) => g.status === "beaten").length;
      return {
        earned: beaten >= 5,
        pct: Math.min(100, Math.round((beaten / 5) * 100)),
        at: beaten + " / 5 beaten",
      };
    },
    // xp-rules.md defines `cert-earned` as "any cert reaches passed", but no rule
    // implemented it, so the gem could never light no matter what you passed.
    "Cert earned": () => {
      const passed = (s.certs || []).filter((c) => c.status === "passed");
      const total = (s.certs || []).length;
      return {
        earned: passed.length > 0,
        pct: passed.length > 0 ? 100 : 0,
        at: total === 0 ? "no certs tracked yet" : passed.length + " of " + total + " held",
      };
    },
  };
  return BADGES.map((b) => {
    const r = rules[b.name];
    const st = r ? r() : { earned: false, pct: 0, at: "not yet" };
    const eng = (s.badgeIds || []).includes(slug(b.name));
    return { ...b, earned: eng || st.earned, pct: eng ? 100 : st.pct, at: eng && st.at === "not yet" ? "earned" : st.at };
  });
}
export function badgesList(s: HudStore): Badge[] {
  return s.live ? computeBadgesLive(s) : BADGES.map((b) => ({ ...b, earned: false, pct: 0, at: "not yet" }));
}

export function computeView(s: HudStore) {
  const empty = !s.live;
  const level = empty ? 1 : s.level;
  const need = xpNeeded(level);
  const xp = empty ? 0 : s.xp;
  const xpToday = empty ? 0 : s.xpToday;
  const L = liveQuests(s);
  const boardXp = empty ? 0 : L.reduce((t, q) => t + q.xp, 0);
  const pinned = empty ? null : s.quests.find((q) => q.pinned && !s.done[q.id]);

  const domains = DOMAINS.map((d) => ({
    name: d.name,
    icon: d.icon,
    sprite: d.sprite,
    // XP EARNED today, the same number the domain page's "XP today" tile shows
    // and the same sense the character header uses. It used to be the open
    // pile, so one domain reported two different figures at once: the card
    // said 0 XP while the page said 100 XP, for the same domain on the same
    // day. Both were "right" under their own label and the pair was useless.
    // One name, one meaning, everywhere.
    xp: empty ? 0 : xpEarnedToday(s, d.name),
    health: (empty ? 4 : d.health) + "%",
    color: empty ? "var(--mut)" : heatColor(d.health),
    last: empty ? "never" : d.last,
    // Glow marks a live domain (DESIGN.md, the Living Glow Rule). The empty
    // state's 4% stub and a Cold domain both represent no activity, so they
    // render flat rather than haloing a sliver that means nothing.
    live: !empty && d.health >= 20,
  }));

  // A daily quest IS today's work, every day, by definition. `when` is derived
  // from the `today` flag alone, and that flag is the anti-procrastination one
  // thing: exactly one quest may hold it (xp-rules.md), and usually nothing
  // does. So the Today slice used to mean "the single pinned quest" and read
  // "Nothing in this slice" over a board of eighteen, including five dailies
  // due that very day. Today now means the pinned one plus every daily.
  const isDaily = (q: { type?: string }) => (q.type || "").toLowerCase() === "daily";
  const inRange = (q: { when: string; type?: string; days?: string }) =>
    s.range === "all" || (s.range === "today" ? q.when === "today" || (isDaily(q) && !restsToday(q)) : q.when !== "all");
  const filtered = (empty ? [] : s.quests).filter((q) => inRange(q) && (s.filter === "All" || q.domain === s.filter));
  const visibleQuests = filtered.filter((q) => !s.done[q.id]);
  const doneQuests = filtered.filter((q) => doneToday(s, q));

  const filters = ["All"].concat(DOMAINS.map((d) => d.name)).map((label) => ({
    label,
    on: s.filter === label,
    fg: s.filter === label ? "var(--on-accent)" : "var(--sub)",
    bg: s.filter === label ? "var(--accent-fill)" : "var(--card2)",
    bd: s.filter === label ? "transparent" : "var(--line)",
  }));
  const ranges = ([["today", "Today"], ["week", "Week"], ["all", "All"]] as [string, string][]).map(([key, label]) => ({
    key,
    label,
    fg: s.range === key ? "var(--on-accent)" : "var(--mut)",
    bg: s.range === key ? "var(--accent-fill)" : "transparent",
  }));

  // A main quest's percentage comes from its own `progress` when the goal sets
  // one, else from its child quests. A goal with NEITHER is untracked, and says
  // so: "no quests yet" with no percentage, rather than a bar pinned at 0%.
  // Those two states look identical and mean opposite things - one is "nothing
  // filed here", the other is "filed and going nowhere" - and a board whose
  // whole job is showing momentum must not confuse them.
  const mainStat = (m: { id: string; progress?: number | null }) => {
    const all = s.quests.filter((q) => servesMain(q, m.id));
    const finished = all.filter((q) => s.done[q.id]);
    const leftXp = all.filter((q) => !s.done[q.id]).reduce((t, q) => t + q.xp, 0);
    const explicit = typeof m.progress === "number" && Number.isFinite(m.progress);
    const pct = explicit
      ? Math.max(0, Math.min(100, Math.round(m.progress as number)))
      : all.length
        ? Math.round((finished.length / all.length) * 100)
        : 0;
    const label = all.length
      ? finished.length + " of " + all.length + " quests done"
      : explicit
        ? pct + "% done"
        : "no quests yet";
    return { pct, label, left: leftXp, explicit, tracked: explicit || all.length > 0 };
  };
  const mkMain = (m: (typeof s.mains)[number]) => {
    const st = mainStat(m);
    const untracked = empty || !st.tracked;
    // A goal is finished when the GOAL says so - status done, or an explicit
    // progress of 100 - never because the quests filed under it happen to all
    // be ticked. Those mean different things: "Ship my own product" is a 2027
    // goal with one finished quest under it, which is a goal in motion, not a
    // goal achieved. Deriving completion from children also made the board
    // eat it: a complete main quest leaves the grid for the trophy shelf, so
    // finishing the first quest under a long-horizon goal shelved the whole
    // goal and left no way back into it.
    // A finite horizon says the goal can end, not that its filed tasks define
    // the whole outcome. An everlasting goal stays on the board even at 100%.
    // Check raw explicit progress: rounding 99.6% to 100% cannot award a trophy.
    const everlasting = m.horizon === "everlasting";
    const complete = !empty && !everlasting &&
      (m.status === "done" || (st.explicit && (m.progress ?? 0) >= 100));
    return {
      id: m.id,
      name: m.name,
      fa: complete ? "fa-solid fa-trophy" : m.fa || "fa-solid fa-circle-question",
      color: complete ? "var(--gold)" : m.color || "var(--mut)",
      pct: (untracked ? 0 : st.pct) + "%",
      // No percentage at all while untracked. "0%" next to "no quests yet" reads
      // as a failed goal; the blank lets the label do the talking.
      pctText: complete ? "Complete" : untracked ? "" : st.pct + "%",
      progress: empty ? "no quests yet" : complete ? (s.finished[m.id] ? "finished " + s.finished[m.id] : "Complete") : st.label,
      border: complete ? "var(--gold)" : "var(--line)",
      bg: complete ? "var(--card)" : "var(--card)",
      complete,
    };
  };
  const allMains = s.mains.map(mkMain);
  const mains = allMains.filter((m) => !m.complete);
  const doneMains = allMains.filter((m) => m.complete);

  const attrSrc = s.live && Array.isArray(s.liveAttrs) ? s.liveAttrs : ATTRS;
  const attrs = attrSrc.map((a) => ({ name: a.name, color: a.color, level: a.level, pct: Math.round((a.into / (a.next || 1)) * 100) + "%" }));

  const badgeView = (b: Badge) => ({
    name: b.name,
    fa: b.fa,
    earned: b.earned,
    // The glyph sits on the gem's own identity fill, all of which are light:
    // white ran 1.63-2.62:1 across the set, --on-accent runs 7.28-11.73:1.
    iconColor: b.earned ? "var(--on-accent)" : "var(--mut)",
    gem: b.earned ? gemFill(b.color) : "var(--raise)",
    shadow: b.earned
      ? "0 0 15px -3px " + b.color + ", inset 1.5px 1.5px 0 rgba(255,255,255,.5), inset -1.5px -1.5px 4px rgba(0,0,0,.4)"
      : "inset 1px 1px 0 rgba(255,255,255,.06)",
    wellBg: b.earned ? "var(--card2)" : "var(--bg2)",
    wellBd: b.earned ? "var(--line)" : "var(--raise)",
  });
  const BL = badgesList(s);
  // Earned first, then closest-to-earned, so the strip answers "what am I near?"
  // rather than showing whatever order the list happens to be in. Twenty fills
  // the card's two rows at desktop width; six left it two-thirds empty.
  const badgeStrip = [...BL]
    .sort((a, b) => Number(b.earned) - Number(a.earned) || (b.pct || 0) - (a.pct || 0))
    .slice(0, 20)
    .map(badgeView);
  const earned = BL.filter((b) => b.earned).length;
  const badge = BL.find((b) => b.name === s.badge);

  const nextTitle = (() => {
    const nx = TITLE_LVLS.find((l) => l > level);
    return nx ? "Next title at Lv " + nx + " · " + TITLES[nx] : "Top title earned";
  })();

  return {
    empty,
    level,
    title: titleFor(level),
    pkBusy: s.pkBusy,
    pkMsg: s.pkMsg,
    pkTone: s.pkTone,
    showPasskey: canPasskey() && !!tokenGet(),
    passkeySupported: canPasskey(),
    passkeyEnrolled: passkeyEnrolled(s.email),
    xpLabel: xp + " / " + need + " XP",
    xpPct: Math.round((xp / need) * 100) + "%",
    nextTitle,
    toNext: need - xp + " to level " + (level + 1),
    streak: empty ? 0 : s.streak,
    xpToday,
    boardXp,
    todayOffset: 490.1 * (1 - Math.min(1, boardXp ? xpToday / (xpToday + boardXp) : 0)),
    burst: !!s.burst,
    burstText: s.burst || "",
    // The flame lives in the top bar, so a completion made while scrolled down
    // would miss its catch entirely. Echo the beat at the ring, where the eye
    // already is, in the flame's own amber.
    burstSecured: s.streakCaught,
    todayLine: s.today.weekday + ", " + s.today.date,
    place: s.today.place,
    weatherFa: s.weather.fa,
    weatherColor: s.weather.color,
    weatherLine: s.weather.temp + " · " + s.weather.label,
    weatherNote: s.weather.note,
    // pinned hero
    hasPin: !!pinned,
    pinTag: pinned ? "You pinned this" : empty ? "Nothing pinned" : "Pin cleared",
    pinMeta: pinned ? pinned.domain + " · 06:40 this morning" : empty ? "Pin one thing and the day has a spine" : "Use the pin on any quest below",
    pinnedTitle: pinned ? pinned.title : empty ? "Pin your first quest" : "Pin the next one",
    pinnedNote: pinned
      ? pinned.note || "Your one next thing for today."
      : empty
        ? "You're level 1 with an empty board. Add a quest, pin it, and the HUD comes alive."
        : "That one's done. Scan the heat row for the coldest domain, then pin what you'll actually do next.",
    pinnedXpLabel: "+" + (pinned ? pinned.xp : 0) + " XP",
    startLabel: pinned ? "Start quest" : empty ? "Add a quest" : "Choose from the board",
    // board
    domains,
    visibleQuests,
    doneQuests,
    filters,
    ranges,
    mains,
    hasDoneMains: doneMains.length > 0,
    shelfCount: doneMains.length + (doneMains.length === 1 ? " trophy" : " trophies"),
    shelfChevron: s.shelfOpen ? "fa-solid fa-chevron-down" : "fa-solid fa-chevron-right",
    // `id` so a trophy is a way back into the goal. Shelved goals rendered as
    // bare text before, which made the shelf a one-way door: the only route to
    // a finished goal's quests and its why was to un-finish it.
    // Goal records have no award date. A missing date cannot mean today.
    shelfChips: (s.shelfOpen ? doneMains : doneMains.slice(0, 4)).map((m) => ({ id: m.id, name: m.name, date: s.finished[m.id] || "Earned" })),
    shelfMore: doneMains.length > 4 && !s.shelfOpen ? "+" + (doneMains.length - 4) + " more" : "",
    hasShelfMore: doneMains.length > 4 && !s.shelfOpen,
    openCount: L.length + " open",
    hasOpenQuests: L.length > 0,
    noQuests: visibleQuests.length === 0,
    emptyTitle: empty ? "Day one. Nothing here yet." : L.length === 0 ? "No open quests" : "Nothing in this slice",
    emptyNote: empty
      ? "This is honestly what level 1 looks like. Add your first quest and watch the board fill."
      : L.length === 0 ? "Add a quest to start something new. Completed quests are in the log."
      : "Try a wider range, or clear the domain filter.",
    hasDone: doneQuests.length > 0,
    doneLabel: doneQuests.length + " done today",
    doneChevron: s.showDone ? "fa-solid fa-chevron-down" : "fa-solid fa-chevron-right",
    showDone: s.showDone,
    attrs,
    badgeStrip,
    badgeCount: earned + " / " + BADGES.length,
    // The streak flame follows any logged quest. The required-daily gate belongs
    // to the reward button below and must not erase a real day of activity.
    ...(() => {
      const active = !empty && s.lastActive === isoToday();
      const n = s.streak;
      return {
        streakLive: active,
        streakCaught: s.streakCaught,
        streakLabel:
          n === 0
            ? "No streak yet. Finish anything today to start one."
            : active
              ? n + (n === 1 ? " day" : " days") + " running. Today is on the board."
              : n + (n === 1 ? " day" : " days") + " running. Nothing logged today yet.",
        streakFg: active ? "var(--orange)" : "var(--faint)",
        streakInk: active ? "var(--ink)" : "var(--mut)",
        streakBd: active ? "var(--warn-line)" : "var(--line)",
        streakBg: active ? "color-mix(in srgb, var(--orange) 12%, transparent)" : "var(--card)",
      };
    })(),
    noBadges: earned === 0,
    badgeHint:
      earned === 0
        ? "None earned yet. Finish any quest and the first gem lights up."
        : "Tap a gem to see how it was earned.",
    // The daily reward follows the server gate: finish every active required
    // daily, or any daily when none is required. Keep it locked while a write is
    // in flight so a just-completed required quest reaches the server before the
    // dependent claim request.
    ...(() => {
      const eligible = !empty && dailyRewardEligible(s.quests, s.done, isoToday());
      const waitingForSave = !s.claimed && eligible && s.saving > 0;
      const locked = !s.claimed && (!eligible || waitingForSave);
      const dim = s.claimed || locked;
      return {
        claimLabel: s.claimed ? "Claimed" : "Claim +40",
        claimIcon: locked ? "fa-solid fa-lock" : "fa-solid fa-gift",
        claimDisabled: dim,
        claimTitle: s.claimed
          ? "Daily reward already claimed"
          : waitingForSave
            ? "Waiting for your completed quest to save"
            : locked
              ? "Complete today's required daily to unlock your +40"
              : "Claim your daily reward",
        claimFg: dim ? "var(--mut)" : "var(--on-accent)",
        claimBg: dim ? "var(--card)" : "var(--accent-fill-deep)",
        claimBd: dim ? "var(--line)" : "transparent",
        claimShadow: "none",
      };
    })(),
    // Persistence, not vault sync. The HUD cannot see Remotely Save, so it
    // reports the only thing it actually knows: did our writes land.
    unsavedCount: s.unsaved.length,
    syncCount: s.unsaved.length
      ? s.unsaved.length + " need retry"
      : s.saving
        ? "Saving"
        : s.live
          ? "Saved"
          : "Offline",
    syncColor: s.unsaved.length ? "var(--danger-ink)" : s.saving ? "var(--orange-ink)" : s.live ? "var(--green-ink)" : "var(--mut)",
    syncBd: s.unsaved.length ? "var(--danger-line)" : s.saving ? "var(--warn-line)" : s.live ? "var(--ok-line)" : "var(--line)",
    syncBg: s.unsaved.length
      ? "color-mix(in srgb, var(--danger) 12%, transparent)"
      : s.saving
        ? "color-mix(in srgb, var(--orange) 12%, transparent)"
        : s.live
          ? "color-mix(in srgb, var(--green) 10%, transparent)"
          : "var(--card)",
    syncFa: s.unsaved.length
      ? "fa-solid fa-cloud-exclamation"
      : s.saving
        ? "fa-solid fa-cloud-arrow-up"
        : "fa-solid fa-cloud",
    syncTitle: s.unsaved.length
      ? s.unsaved.map((u) => u.label + (u.detail ? ": " + u.detail : "")).join("; ") + " Open the vault to retry pending changes."
      : s.saving
        ? "Saving your last change"
        : s.live
          ? "Every change so far is saved"
          : "Working from local state, nothing is being saved",
    // overlays
    levelUp: s.levelUp,
    levelUpNote: s.levelUpNote,
    confetti: s.levelUp
      ? Array.from({ length: 18 }, (_, i) => ({
          left: 5 + i * 5.2 + "%",
          dx: ((i * 37) % 90) - 45 + "px",
          color: (["var(--accent)", "var(--orange)", "var(--green)", "var(--pink)", "var(--gold)"] as const)[i % 5],
          dur: 1.5 + (i % 4) * 0.35 + "s",
          delay: (i % 6) * 0.09 + "s",
        }))
      : [],
    mainDoneOpen: !!s.mainDone,
    mainDoneName: s.mains.find((m) => m.id === s.mainDone)?.name || "",
    mainDoneNote: (() => {
      const m = s.mains.find((x) => x.id === s.mainDone);
      if (!m) return "";
      const qs = s.quests.filter((q) => servesMain(q, m.id));
      return qs.length + " quests · " + qs.reduce((t, q) => t + q.xp, 0) + " XP, start to finish.";
    })(),
    badge,
    badgeGem: badge ? (badge.earned ? gemFill(badge.color) : "var(--raise)") : "var(--raise)",
    badgeGlow: badge ? badge.color : "var(--accent)",
    // The glyph sits on the gem's own identity fill, all of which are light.
    badgeIconColor: badge && badge.earned ? "var(--on-accent)" : "var(--mut)",
    badgeState: badge ? (badge.earned ? "Earned" : "Locked") : "",
  };
}

export type HudView = ReturnType<typeof computeView>;
